import { Badge, Button, Col, List, Row, Typography } from "antd";
import i18n from "i18next";
import { type FC, useState } from "react";

import { DashboardCardContainer } from "src/app/features/Dashboard/utils/dashboardStyles";
import { Palette } from "../../../../core/styles/palette";
import type { JobExecutionDto } from "../types/Dashboard";
import { formatRelativeTime } from "../utils/formatters";

const { Text } = Typography;
const DEFAULT_VISIBLE = 5;

interface RecentJobExecutionsCardProps {
    jobExecutions: JobExecutionDto[];
}

/**
 * RecentJobExecutionsCard - Displays recent job executions with freshness indicators
 */
export const RecentJobExecutionsCard: FC<RecentJobExecutionsCardProps> = ({ jobExecutions }) => {
    const [showAll, setShowAll] = useState(false);

    const getJobFreshnessColor = (lastRun?: string): string | undefined => {
        if (!lastRun) return undefined;
        const now = new Date();
        const lastRunDate = new Date(lastRun);
        const hoursDiff = (now.getTime() - lastRunDate.getTime()) / (1000 * 60 * 60);

        if (hoursDiff < 1) return Palette.HeroGreen;
        if (hoursDiff < 6) return Palette.Warning;
        if (hoursDiff < 24) return Palette.Warning;
        return Palette.AeviRed;
    };

    const visibleJobs = showAll ? jobExecutions : jobExecutions.slice(0, DEFAULT_VISIBLE);
    const hasMore = jobExecutions.length > DEFAULT_VISIBLE;

    return (
        <DashboardCardContainer title={i18n.t("dashboard.recentJobExecutions.title")}>
            <List
                size="small"
                dataSource={visibleJobs}
                renderItem={(job: JobExecutionDto) => {
                    const color = getJobFreshnessColor(job.lastRun);
                    return (
                        <List.Item>
                            <Row justify="space-between" style={{ width: "100%" }}>
                                <Col>
                                    <Text strong>{job.jobName}</Text>
                                </Col>
                                <Col>
                                    {job.lastRun ? (
                                        <Badge color={color} text={formatRelativeTime(job.lastRun)} />
                                    ) : (
                                        <Text type="secondary">{i18n.t("dashboard.recentJobExecutions.noData")}</Text>
                                    )}
                                </Col>
                            </Row>
                        </List.Item>
                    );
                }}
            />
            {hasMore && (
                <Row justify="center" style={{ marginTop: 8 }}>
                    <Button type="link" size="small" onClick={() => setShowAll((prev) => !prev)}>
                        {showAll
                            ? i18n.t("dashboard.showLess")
                            : i18n.t("dashboard.recentJobExecutions.showMore", { count: jobExecutions.length - DEFAULT_VISIBLE })}
                    </Button>
                </Row>
            )}
        </DashboardCardContainer>
    );
};
